// Recipe Book — full-screen overlay opened from UserProfile that
// renders the viewer's saved recipes (custom + AI) as a turnable
// book. Each recipe gets one page; cover + index sit ahead of them.
//
// Design notes:
//   - Portaled to document.body to escape the App-level <motion.div>
//     transform that breaks position:fixed inset:0 inside the tab
//     wrapper (same gotcha that bit MealDetailDrawer earlier).
//   - Reads via useRecipeBook (clean-room hook, not useUserRecipes —
//     the existing hook has been buggy and the picker keeps using it).
//   - Page-turn motion: rotateY (-90 → 0) with axis-vertical perspective
//     on the parent. Direction-aware so going forward and back read
//     as opposite hinges. Pure framer-motion; no GPU surprises.
//   - Typography uses ONLY three families per the project's locked
//     hierarchy (memory/feedback_typography_reuse.md):
//       1. Pale Martini  (FONT.display)   — surface title only
//          ("RECIPE BOOK"). Same role Pale Martini plays for
//          "The Kitchen" / "Home" elsewhere in the app.
//       2. New Kansas    (FONT.itemTitle) — RECIPE NAMES only.
//          The dish title on each page + the entries in the TOC.
//          italic 500. This is the editorial "this is the THING"
//          voice; nothing else gets it.
//       3. Forma DJR Deck (FONT.forma)    — everything else.
//          Kickers, page numbers, body copy, descriptions, button
//          labels, ingredient lines, the cover's viewer-name line.
//          Hierarchy comes from weight/italic/tracking, not from
//          reaching for a different family.
//     No Fraunces. No DM Mono. No DM Sans. No instrument-serif.
//     No emojis.
//   - The book is paper-textured (warm cream with a subtle gradient)
//     and sits over the WarmBackdrop. Spine shadow on the left edge,
//     page-corner curl at the bottom-right.
//   - Swipe gestures (touch + mouse drag) plus arrow buttons + page
//     number indicator. Tap a recipe in the index to jump straight to
//     its page.

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { FONT, COLOR } from "../lib/tokens";
import { applyPreferredUnit, useUnitPrefsVersion, DISPLAY_CONTEXT } from "../lib/unitPrefs";
import { totalTimeMin, difficultyLabel } from "../data/recipes";
import { scaleRecipe } from "../lib/recipeScaling";
import { useRecipeBook } from "../lib/useRecipeBook";
import { useToast } from "../lib/toast";
import { createRecipeShare } from "../lib/recipeShares";
import { Share } from "@capacitor/share";
import ShareRecipeSheet from "./ShareRecipeSheet";
import { useRelationships } from "../lib/useRelationships";
import { useFamilyCookStats } from "../lib/useFamilyCookStats";
import { useRecipeCookStats, relativeTimeShort } from "../lib/useRecipeCookStats";
import { useBrandNutrition } from "../lib/useBrandNutrition";
import { useIngredientInfo } from "../lib/useIngredientInfo";
import RecipeNutritionLine from "./RecipeNutritionLine";
import RecipePairingCard from "./RecipePairingCard";
import RecipeNarrationButton from "./RecipeNarrationButton";
import RecipeAudioPlayer from "./RecipeAudioPlayer";
import RecipePhotoStrip from "./RecipePhotoStrip";
import RecipeKidMenuSection from "./RecipeKidMenuSection";
import RecipeFlipCard from "./RecipeFlipCard";
import RecipePageEditor from "./RecipePageEditor";
import RecipeReviseSheet from "./RecipeReviseSheet";
import RecipeVersionsSheet from "./RecipeVersionsSheet";
import { useProfile } from "../lib/useProfile";
import { absoluteAvatarURL } from "../lib/absoluteAvatarURL";
import AddRecipeFromSheet from "./AddRecipeFromSheet";
import IndexPage from "./RecipeBookIndex";
import RecipeSideDishSection, { PairedWithAttribution } from "./RecipeSideDishSection";
import { runBackgroundRecipeGen, retryBackgroundRecipeGen } from "../lib/backgroundRecipeGen";
import ServingsStepper from "./ServingsStepper";

// Page-turn transition. ease curve mirrors the iOS-drawer curve used
// elsewhere in the project. duration biased a touch slow because a
// snappy book-page feels wrong; readers expect a deliberate flip.
const TURN = { duration: 0.55, ease: [0.32, 0.72, 0, 1] };

// Last-viewed page memory — small localStorage helpers so a user
// who closes mid-flip can land back where they were on next open.
// 24h TTL because "where I was last week" surprises rather than
// helps. Per-user key so device-shared accounts don't bleed.
const LAST_PAGE_KEY = (uid) => `recipeBook.lastPage.v1:${uid}`;
const LAST_PAGE_TTL_MS = 24 * 60 * 60 * 1000;

function readLastPage(userId) {
  if (typeof window === "undefined" || !userId) return null;
  try {
    const raw = window.localStorage?.getItem(LAST_PAGE_KEY(userId));
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p || typeof p.pageIndex !== "number" || typeof p.savedAt !== "number") return null;
    if (Date.now() - p.savedAt > LAST_PAGE_TTL_MS) return null;
    return p;
  } catch { return null; }
}

function writeLastPage(userId, pageIndex) {
  if (typeof window === "undefined" || !userId) return;
  try {
    window.localStorage?.setItem(
      LAST_PAGE_KEY(userId),
      JSON.stringify({ pageIndex, savedAt: Date.now() }),
    );
  } catch { /* quota / private mode — non-fatal */ }
}

// Direction-aware variants. variant key is `direction` — set to
// "next" on forward turn, "prev" on backward.
const pageVariants = {
  enter: (dir) => ({
    rotateY: dir === "next" ? 85 : -85,
    opacity: 0,
    transition: TURN,
  }),
  center: {
    rotateY: 0,
    opacity: 1,
    transition: TURN,
  },
  exit: (dir) => ({
    rotateY: dir === "next" ? -85 : 85,
    opacity: 0,
    transition: TURN,
  }),
};

const SWIPE_THRESHOLD_PX = 60;
const SWIPE_VELOCITY = 0.3;

export default function RecipeBook({
  open,
  onClose,
  userId,
  // Viewer's allergies — CONTAINS-X warn chips on TOC cards (warn,
  // don't hide: silently missing templates read as a broken catalog).
  viewerAllergies = [],
  viewerName,
  hasFamily = false,
  isAdmin = false,
  onCook,
  onSchedule,
  // Retry a failed background recipe generation. (stubId) => void —
  // re-runs the detached job from the inputs stashed at first-run time.
  // No-op when absent (e.g. the UserProfile-owned book instance).
  onRetryGenerate,
  // Auto-open the paste-recipe sheet on mount. CreateMenu's "Add a
  // recipe" branch passes true so the user lands directly in the
  // paste flow instead of having to find the (+) button. Default
  // false preserves the legacy entrypoint (avatar → Recipe Book
  // lands on the cover).
  initialAddOpen = false,
  // Deep-link target — when set, the book opens to the recipe with
  // this slug (skips cover + TOC). Used by kid_meal_request
  // notification taps so the parent lands directly on the recipe
  // the kid asked for. No-op when the slug isn't found.
  initialSlug = null,
  // Pantry threaded through for the per-recipe calorie/macro rollup
  // on RecipePage. Empty array works fine — recipeNutrition falls
  // back to canonical-only nutrition when no pantry row matches.
  pantry = [],
  // Photo-mode handoff from the universal AddRecipeFromSheet. Parent
  // closes the book and routes the seed into CreateMenu's AI sub-phase
  // (same destination CreateMenu uses for its own photo flow).
  onPhotoSeed,
}) {
  const {
    recipes, loading, refresh,
    saveRecipe, deleteRecipe, setSharing, setCommunityPublished,
    setFavorite, setNotes, setTags, setTitle, setPairings,
    setSideDishes,
    updateRecipeContent,
  } = useRecipeBook(userId);
  // Paste-recipe sheet — opens from the + button in the header,
  // OR from CreateMenu's direct route via initialAddOpen.
  const [addOpen, setAddOpen] = useState(!!initialAddOpen);
  const { stats: cookStats } = useRecipeCookStats(userId);
  const [pageIndex, setPageIndex] = useState(0);
  const [direction, setDirection] = useState("next");
  const [confirmingDelete, setConfirmingDelete] = useState(null);
  // AI revise sheet — set to the user_recipes row being revised.
  // The sheet itself gates the (paid) AI call behind its explicit
  // REVISE WITH AI button; opening it costs nothing.
  const [reviseFor, setReviseFor] = useState(null);
  // Resume offer — populated on open from localStorage. The cover
  // shows a "resume on page X" banner if this is set; clears once
  // the user takes (or declines) it.
  const [resumeOffer, setResumeOffer] = useState(null);

  // Fresh fetch on open — guards against the user opening the book
  // from a stale state (e.g. they saved a recipe in another tab).
  useEffect(() => {
    if (open) {
      setPageIndex(0);
      setDirection("next");
      setConfirmingDelete(null);
      // Read last-viewed page. Show banner only if it points past
      // the cover (otherwise "resume on page 1" is the cover and
      // we're already there).
      const last = readLastPage(userId);
      setResumeOffer(last && last.pageIndex > 0 ? last : null);
      refresh();
    }
  }, [open, refresh, userId]);

  // Deep-link to a specific recipe. Runs when recipes load AND
  // initialSlug is set. Page math: 0 = cover, 1 = TOC, 2+ = recipes,
  // so the target page is (index of slug in recipes) + 2. No-op
  // when the slug isn't in the list (e.g. shared family recipe the
  // viewer can't see, or the slug got renamed).
  //
  // Applied-once guard: `recipes` is a dep (the target row may load
  // after open), but the jump must fire once per (open, slug) — NOT
  // on every later recipes mutation. Without the guard, any prepend /
  // realtime update while the book is open (e.g. minting a side-dish
  // stub, or its ready write landing) re-ran this and teleported the
  // reader back to the deep-linked page.
  const appliedSlugRef = useRef(null);
  useEffect(() => {
    if (!open) { appliedSlugRef.current = null; return; }
    if (!initialSlug || !Array.isArray(recipes) || recipes.length === 0) return;
    if (appliedSlugRef.current === initialSlug) return;
    const idx = recipes.findIndex((r) => r?.slug === initialSlug);
    if (idx >= 0) {
      appliedSlugRef.current = initialSlug;
      setPageIndex(idx + 2);
      setDirection("next");
      setResumeOffer(null);
    }
  }, [open, initialSlug, recipes]);

  // Persist current page so the next open can offer resume. Only
  // writes when overlay is open (don't churn during background tabs)
  // and writes the LATEST committed page after a 400ms debounce so
  // a flurry of arrow-key turns doesn't spam localStorage.
  useEffect(() => {
    if (!open || !userId) return;
    const t = setTimeout(() => writeLastPage(userId, pageIndex), 400);
    return () => clearTimeout(t);
  }, [open, userId, pageIndex]);

  // Page model: index 0 = cover, index 1 = TOC (or empty state),
  // indices 2..N+1 = one recipe each.
  const totalPages = recipes.length === 0 ? 1 : 2 + recipes.length;
  const turnTo = (next) => {
    const clamped = Math.max(0, Math.min(totalPages - 1, next));
    if (clamped === pageIndex) return;
    setDirection(clamped > pageIndex ? "next" : "prev");
    setPageIndex(clamped);
    setConfirmingDelete(null);
  };
  const next = () => turnTo(pageIndex + 1);
  const prev = () => turnTo(pageIndex - 1);

  // ── Side-dish generation orchestrator ────────────────────────────
  // Sequence: mint the pending stub → compensate the page index (the
  // stub PREPENDS to `recipes`, which would silently shift the page
  // under the user) → link it on the main row (the SIDE DISHES
  // section shows the pending entry immediately) → fire the detached
  // background generation. The carry.pairedWith in stubMeta survives
  // the pipeline's wholesale jsonb rewrites.
  const handleGenerateSide = async ({ mainRow, stubRecipe, payload, finalizeInputs, stubMeta }) => {
    const row = await saveRecipe(stubRecipe, "ai");
    setPageIndex((p) => (p >= 2 ? p + 1 : p));
    const prevSides = Array.isArray(mainRow.recipe?.sideDishes)
      ? mainRow.recipe.sideDishes
      : [];
    try {
      await setSideDishes(mainRow.id, [
        ...prevSides,
        { slug: row.slug, title: stubRecipe.title },
      ]);
    } catch (e) {
      // The stub still lands in the book, just unlinked — don't block
      // the generation on a link-write hiccup.
      console.warn("[RecipeBook] side link write failed:", e?.message || e);
    }
    runBackgroundRecipeGen({ stubId: row.id, payload, finalizeInputs, stubMeta });
  };

  // Retry for failed background stubs. The App-owned instance passes
  // onRetryGenerate; the UserProfile-owned instance doesn't — fall
  // back to the pipeline's own retry (reads the localStorage cache)
  // so RETRY works everywhere.
  const handleRetryGenerate = onRetryGenerate
    || ((stubId) => retryBackgroundRecipeGen(stubId));

  // Keyboard nav while the overlay is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
      else if (e.key === "Escape") { e.preventDefault(); onClose?.(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, pageIndex, totalPages]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const currentRecipe = pageIndex >= 2 ? recipes[pageIndex - 2] : null;

  return createPortal(
    <div
      style={{
        position: "fixed", inset: 0,
        zIndex: 320,
        background: "rgba(20, 14, 8, 0.62)",
        backdropFilter: "blur(8px) saturate(140%)",
        WebkitBackdropFilter: "blur(8px) saturate(140%)",
        display: "flex", flexDirection: "column",
        maxWidth: 480, margin: "0 auto",
        padding: "calc(env(safe-area-inset-top, 0px) + 16px) 20px calc(env(safe-area-inset-bottom, 0px) + 16px)",
      }}
    >
      {/* Header — title kicker + close. Lives outside the book so the
          page-turn animation doesn't drag it. */}
      <Header
        viewerName={viewerName}
        recipeCount={recipes.length}
        loading={loading}
        onAdd={() => setAddOpen(true)}
        onClose={onClose}
      />

      {/* The book itself. perspective on this wrapper is what makes
          rotateY on pages feel 3D. value tuned tight enough to read
          like a real flip without distorting type. */}
      <div
        style={{
          flex: 1,
          marginTop: 18,
          position: "relative",
          perspective: 1400,
          perspectiveOrigin: "50% 50%",
        }}
      >
        {/* Spine shadow on the left edge for depth. Always present;
            fixed-position relative to the book frame. */}
        <div
          aria-hidden
          style={{
            position: "absolute", left: 0, top: 0, bottom: 0,
            width: 24, pointerEvents: "none",
            background: "linear-gradient(90deg, rgba(60,40,18,0.35) 0%, rgba(60,40,18,0) 100%)",
            zIndex: 1,
          }}
        />
        <AnimatePresence mode="popLayout" custom={direction} initial={false}>
          <motion.div
            // Keyed by the RECIPE on recipe pages (cover/TOC stay
            // positional) so an index-compensating shift after a
            // side-dish stub prepends doesn't replay a full page-turn
            // flip of identical content. Normal turns still animate —
            // the recipe id changes.
            key={pageIndex <= 1 ? `static-${pageIndex}` : (currentRecipe?.id || `page-${pageIndex}`)}
            custom={direction}
            variants={pageVariants}
            initial="enter"
            animate="center"
            exit="exit"
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragEnd={(_, info) => {
              const offset = info.offset.x;
              const velocity = info.velocity.x;
              if (offset < -SWIPE_THRESHOLD_PX || velocity < -SWIPE_VELOCITY * 1000) next();
              else if (offset > SWIPE_THRESHOLD_PX || velocity > SWIPE_VELOCITY * 1000) prev();
            }}
            style={{
              position: "absolute", inset: 0,
              transformStyle: "preserve-3d",
              transformOrigin: "left center",
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              borderRadius: 14,
              overflow: "hidden",
              cursor: "grab",
              // Paper. Warm cream with a subtle vertical gradient so the
              // page reads as physical material, not a flat rectangle.
              background:
                "linear-gradient(180deg, #FBF5E8 0%, #F5EDDB 100%)",
              boxShadow:
                "0 24px 48px -12px rgba(60,40,18,0.35), 0 8px 16px -8px rgba(60,40,18,0.25), inset 0 1px 0 rgba(255,255,255,0.9)",
            }}
          >
            <PageBody
              viewerAllergies={viewerAllergies}
              pageIndex={pageIndex}
              totalPages={totalPages}
              recipes={recipes}
              loading={loading}
              recipe={currentRecipe}
              viewerName={viewerName}
              userId={userId}
              hasFamily={hasFamily}
              isAdmin={isAdmin}
              pantry={pantry}
              cookStats={cookStats}
              onJumpTo={turnTo}
              // `scaled` is the servings-adjusted copy from the page's
              // stepper (scaleRecipe output; === row.recipe when
              // untouched). Passing it through is what makes the book's
              // SERVES choice actually reach CookMode / SchedulePicker
              // instead of silently reverting to the saved serves.
              onCook={(r, scaled) => onCook?.(scaled || r.recipe)}
              onSchedule={(r, scaled) => onSchedule?.(scaled || r.recipe)}
              onShareToggle={(r) => setSharing(r.id, { shared: !r.shared })}
              onPublishToggle={(r) => setCommunityPublished(r.id, !r.communityPublished)}
              onFavoriteToggle={(r) => setFavorite(r.id, !r.isFavorite)}
              onSetNotes={(r, notes) => setNotes(r.id, notes)}
              onSetTags={(r, tags) => setTags(r.id, tags)}
              onRename={(r, newTitle) => setTitle(r.id, newTitle)}
              onSetPairings={(r, pairings) => setPairings(r.id, pairings)}
              onDelete={(r) => setConfirmingDelete(r.id)}
              onUpdateContent={(r, nextRecipe) => updateRecipeContent(r.id, nextRecipe)}
              onRetryGenerate={handleRetryGenerate}
              onGenerateSide={handleGenerateSide}
              onForkSave={async (payload, source) => {
                // Fork lands at the front of `recipes` (saveRecipe
                // prepends), so page index 2 is the new recipe.
                await saveRecipe(payload, source || "custom");
                turnTo(2);
              }}
              onRevise={(r) => setReviseFor(r)}
            />
            {/* Page corner curl — bottom-right, suggesting "more pages."
                Hidden on the last page. */}
            {pageIndex < totalPages - 1 && (
              <CornerCurl />
            )}
          </motion.div>
        </AnimatePresence>

        {/* Resume offer — only visible on the cover (pageIndex 0)
            and only when the last-viewed page lands inside the
            current book bounds. Lets the user jump back to where
            they were on a recent open without flipping through.
            Floats over the cover but inside the book frame so it
            feels like a bookmark slipped into the cover. */}
        {pageIndex === 0 && resumeOffer && resumeOffer.pageIndex < totalPages && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
            style={{
              position: "absolute",
              left: "50%", bottom: 18,
              transform: "translateX(-50%)",
              zIndex: 4,
              display: "flex", alignItems: "center", gap: 8,
              padding: "8px 8px 8px 14px",
              background: "rgba(20, 14, 6, 0.78)",
              border: "1px solid rgba(255, 248, 224, 0.22)",
              borderRadius: 999,
              backdropFilter: "blur(6px)",
              WebkitBackdropFilter: "blur(6px)",
              boxShadow: "0 6px 14px rgba(20,12,4,0.30)",
            }}
          >
            <span style={{
              fontFamily: FONT.forma, fontSize: 12, fontWeight: 500,
              color: "rgba(255,248,224,0.85)",
              letterSpacing: "0.06em",
            }}>
              Resume on page {String(resumeOffer.pageIndex + 1).padStart(2, "0")}
            </span>
            <button
              onClick={() => {
                turnTo(resumeOffer.pageIndex);
                setResumeOffer(null);
              }}
              style={{
                padding: "5px 12px",
                background: "#2F8F83",
                border: "1px solid #246A60",
                color: "#FFFFFF",
                borderRadius: 999,
                fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
                letterSpacing: "0.14em", textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              Resume
            </button>
            <button
              onClick={() => setResumeOffer(null)}
              aria-label="Dismiss resume offer"
              style={{
                width: 22, height: 22, borderRadius: 11,
                background: "transparent",
                border: "1px solid rgba(255,248,224,0.30)",
                color: "rgba(255,248,224,0.75)",
                fontSize: 12, lineHeight: 1, padding: 0, cursor: "pointer",
              }}
            >
              ×
            </button>
          </motion.div>
        )}
      </div>

      {/* Navigation bar — page number + arrows. Outside the book so
          it stays put while pages turn. */}
      <NavBar
        pageIndex={pageIndex}
        totalPages={totalPages}
        onPrev={prev}
        onNext={next}
      />

      {/* Add-recipe sheet — opens from the + button in the header.
          Universal hub: photo / video link / paste-text. Same
          component CreateMenu's "Add a Recipe" tile uses, so the
          two entrypoints stay in lockstep. Photo mode hands the
          identified items up via onPhotoSeed; the parent routes
          the seed into CreateMenu's AI sub-phase. */}
      <AddRecipeFromSheet
        userId={userId}
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSave={async (recipe) => {
          await saveRecipe(recipe, "custom");
          setAddOpen(false);
          // Jump to the TOC so the user can see their freshly-added
          // page in context. The new row sits at index 0 of recipes
          // (saveRecipe inserts at the front), so page index 2 is
          // the first recipe page after cover + TOC. We could jump
          // straight to the recipe page (page 2), but landing on
          // TOC lets the user see the NEW pill on their entry.
          turnTo(1);
        }}
        onPhotoSeed={(items) => {
          setAddOpen(false);
          onPhotoSeed?.(items);
        }}
      />

      {/* AI revise sheet — "too spicy, take the heat down" → reviewed
          diff → smart-hybrid save (own row updates in place, anything
          else saves as the viewer's version with provenance). */}
      <RecipeReviseSheet
        open={!!reviseFor}
        onClose={() => setReviseFor(null)}
        baseRecipe={reviseFor?.recipe || null}
        bookRow={reviseFor}
        userId={userId}
        onSaveInPlace={(id, nextRecipe) => updateRecipeContent(id, nextRecipe)}
        onForkSave={async (payload, source) => {
          await saveRecipe(payload, source || "custom");
          turnTo(2);
        }}
      />

      {/* Delete confirmation. Modal over the book; portaled would be
          fine but this overlay is already fixed-positioned, so a
          nested fixed sheet works. */}
      {confirmingDelete && (
        <DeleteConfirm
          recipe={recipes.find((r) => r.id === confirmingDelete)}
          onCancel={() => setConfirmingDelete(null)}
          onConfirm={async () => {
            const id = confirmingDelete;
            setConfirmingDelete(null);
            try {
              await deleteRecipe(id);
              // If we just deleted the current page's recipe, step
              // back so we don't render a stale gone-row.
              if (pageIndex >= 2 + recipes.findIndex((r) => r.id === id)) {
                setDirection("prev");
                setPageIndex((p) => Math.max(1, p - 1));
              }
            } catch {
              // setSharing/deleteRecipe already log; swallow here.
            }
          }}
        />
      )}
    </div>,
    document.body,
  );
}

// ── Header ───────────────────────────────────────────────────────────

function Header({ viewerName, recipeCount, loading, onAdd, onClose }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div>
        <div style={{
          fontFamily: FONT.forma, fontSize: 12, color: "#F5EDDB",
          letterSpacing: "0.16em", opacity: 0.75,
        }}>
          {viewerName ? `${viewerName.toUpperCase()}'S` : "YOUR"}
        </div>
        <div style={{
          fontFamily: FONT.display, fontSize: 28,
          fontWeight: 500, color: "#F8F1DC", lineHeight: 1.0, marginTop: 4,
          letterSpacing: "-0.01em",
        }}>
          Recipe Book
        </div>
        <div style={{
          fontFamily: FONT.forma, fontSize: 12, color: "rgba(245,237,219,0.55)",
          letterSpacing: "0.10em", marginTop: 4,
        }}>
          {loading
            ? "LOADING…"
            : recipeCount === 0
              ? "EMPTY"
              : `${recipeCount} RECIPE${recipeCount === 1 ? "" : "S"}`}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {onAdd && (
          <button
            onClick={onAdd}
            aria-label="Add a new recipe"
            style={{
              width: 38, height: 38, borderRadius: 19,
              background: "rgba(47,143,131,0.45)",
              border: "1px solid rgba(124,224,210,0.50)",
              color: "#F8F1DC", cursor: "pointer",
              fontFamily: FONT.forma, fontSize: 22, fontWeight: 500,
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 0, lineHeight: 1,
              boxShadow: "0 4px 10px rgba(47,143,131,0.32)",
            }}
          >
            +
          </button>
        )}
        <button
          onClick={onClose}
          aria-label="Close recipe book"
          style={{
            width: 38, height: 38, borderRadius: 19,
            background: "rgba(245,237,219,0.15)",
            border: "1px solid rgba(245,237,219,0.30)",
            color: "#F8F1DC", cursor: "pointer",
            fontFamily: FONT.forma, fontSize: 16, fontWeight: 500,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 0, lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}

// ── Generating / failed stub page ───────────────────────────────────
// Shown for a recipe row whose recipe._gen is "pending" or "failed" —
// the background-generation pipeline (src/lib/backgroundRecipeGen.js).
// Pending shows a calm "writing your recipe" state; failed shows the
// reason + a Retry that re-runs the detached job. Same paper + ink as
// the rest of the book so the stub feels like a page, not a modal.
function GeneratingRecipePage({
  recipe, status, pageNumber, totalRecipes, onContents, onRetry, onDelete,
}) {
  const base = recipe.recipe || {};
  const failed = status === "failed";
  // Inline note when a retry can't start. retryBackgroundRecipeGen
  // returns false when its localStorage inputs are gone (evicted,
  // other device) — without this the RETRY tap would be a silent
  // no-op. The App-owned handler navigates away / returns undefined,
  // so the note never shows on that path.
  const [retryNote, setRetryNote] = useState("");
  useEffect(() => { setRetryNote(""); }, [recipe.id]);
  const handleRetryTap = async () => {
    const ok = await onRetry?.();
    if (ok === false) {
      setRetryNote("This draft can't restart from this device — remove it below and regenerate from Create.");
    }
  };
  // Time-based progress while pending. The background-generation path
  // runs detached with no streamed milestones to show, so we calibrate a
  // progress estimate from when generation started (_genStartedAt). It's
  // honest "roughly this far along, ~this long left" feedback instead of
  // an opaque spinner — the thing the user asked for. Decelerating curve
  // (1 − e^(−t/τ)) capped below 100% so it never claims done before the
  // recipe actually lands; the row flipping to a real recipe unmounts
  // this page.
  const [nowTs, setNowTs] = useState(() => Date.now());
  useEffect(() => {
    if (failed) return undefined;
    const id = setInterval(() => setNowTs(Date.now()), 250);
    return () => clearInterval(id);
  }, [failed]);
  const startedAt = base._genStartedAt ? new Date(base._genStartedAt).getTime() : null;
  const elapsedMs = startedAt && Number.isFinite(startedAt) ? Math.max(0, nowTs - startedAt) : null;
  const GEN_EXPECTED_MS = 45000;
  const genProgress = elapsedMs != null
    ? Math.min(0.95, 1 - Math.exp(-elapsedMs / (GEN_EXPECTED_MS * 0.5)))
    : null;
  const genRemainSec = genProgress != null
    ? Math.max(1, Math.round((1 - genProgress) * (GEN_EXPECTED_MS / 1000)))
    : null;
  const genEtaLabel = genProgress == null
    ? null
    : (genProgress > 0.86 ? "almost there…" : `about ${genRemainSec}s left`);
  return (
    <div style={{
      height: "100%", display: "flex", flexDirection: "column",
      padding: "34px 30px 26px", boxSizing: "border-box",
    }}>
      <style>{`@keyframes rbGenPulse { 0%,100% { opacity: 0.25; } 50% { opacity: 1; } }
        @keyframes rbGenSpin { to { transform: rotate(360deg); } }`}</style>
      <button
        onClick={onContents}
        style={{
          alignSelf: "flex-start", background: "transparent", border: "none",
          fontFamily: FONT.forma, fontSize: 12, fontWeight: 600, color: "#9C7E40",
          letterSpacing: "0.14em", cursor: "pointer", padding: 0, marginBottom: 18,
        }}
      >
        ‹ CONTENTS
      </button>

      <div style={{
        flex: 1, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", textAlign: "center", gap: 18,
      }}>
        {failed ? (
          <div style={{ fontSize: 34, lineHeight: 1 }}>{base.emoji || "🍳"}</div>
        ) : (
          <div style={{
            width: 46, height: 46, borderRadius: "50%",
            border: "3px solid rgba(124,94,44,0.18)",
            borderTopColor: "#C39023",
            animation: "rbGenSpin 0.9s linear infinite",
          }} />
        )}

        <div style={{
          fontFamily: FONT.itemTitle, fontStyle: "italic", fontSize: 26,
          color: "#3A2A12", lineHeight: 1.1, maxWidth: 320,
        }}>
          {base.title || "Your recipe"}
        </div>

        {failed ? (
          <>
            <div style={{
              fontFamily: FONT.forma, fontSize: 13, color: "#9A3B2C",
              lineHeight: 1.5, maxWidth: 300,
            }}>
              {base._genError || "Generation didn't finish."}
            </div>
            <button
              onClick={handleRetryTap}
              className="mcm-focusable"
              style={{
                marginTop: 4, padding: "12px 28px",
                background: "#2F8F6B", color: "#FFFFFF", border: "none",
                borderRadius: 12, fontFamily: FONT.forma, fontSize: 13, fontWeight: 700,
                letterSpacing: "0.06em", cursor: "pointer",
              }}
            >
              RETRY
            </button>
            {retryNote && (
              <div style={{
                fontFamily: FONT.forma, fontSize: 12, color: "#9A3B2C",
                lineHeight: 1.5, maxWidth: 300,
              }}>
                {retryNote}
              </div>
            )}
          </>
        ) : (
          <>
            <div style={{
              fontFamily: FONT.forma, fontSize: 13, color: "#7C5E2C",
              letterSpacing: "0.04em",
            }}>
              <span style={{ animation: "rbGenPulse 1.2s ease-in-out infinite" }}>
                Writing your recipe…
              </span>
            </div>
            {genProgress != null && (
              <div style={{ width: "100%", maxWidth: 280 }}>
                <div style={{
                  height: 7, borderRadius: 999,
                  background: "rgba(124,94,44,0.16)", overflow: "hidden",
                }}>
                  <div style={{
                    height: "100%", width: `${Math.round(genProgress * 100)}%`,
                    borderRadius: 999, background: COLOR.leaf,
                    transition: "width 300ms cubic-bezier(0.22,1,0.36,1)",
                  }} />
                </div>
                <div style={{
                  display: "flex", justifyContent: "space-between", marginTop: 6,
                  fontFamily: FONT.forma, fontSize: 12, color: "#9C7E40",
                }}>
                  <span>{Math.round(genProgress * 100)}%</span>
                  <span>{genEtaLabel}</span>
                </div>
              </div>
            )}
            <div style={{
              fontFamily: FONT.forma, fontSize: 12, color: "#9C7E40",
              lineHeight: 1.5, maxWidth: 280,
            }}>
              You can leave this page — even close the app. It'll be here when it's ready.
            </div>
          </>
        )}
      </div>

      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginTop: 18,
      }}>
        <button
          onClick={onDelete}
          style={{
            background: "transparent", border: "none",
            fontFamily: FONT.forma, fontSize: 12, fontWeight: 600, color: "#B0926A",
            letterSpacing: "0.10em", cursor: "pointer", padding: 0,
          }}
        >
          DISCARD
        </button>
        <span style={{
          fontFamily: FONT.forma, fontSize: 12, color: "#B0926A", letterSpacing: "0.10em",
        }}>
          {pageNumber} / {totalRecipes}
        </span>
      </div>
    </div>
  );
}

// ── Page body — dispatches to cover / index / recipe / empty ─────────

function PageBody({
  pageIndex, totalPages, recipes, loading,
  recipe, viewerName, userId, hasFamily, isAdmin = false, pantry = [], cookStats, viewerAllergies = [],
  onJumpTo, onCook, onSchedule, onShareToggle, onPublishToggle,
  onFavoriteToggle, onSetNotes, onSetTags, onRename, onSetPairings, onDelete,
  onUpdateContent, onForkSave, onRevise, onRetryGenerate, onGenerateSide,
}) {
  // Cover page (always index 0).
  if (pageIndex === 0) {
    return <CoverPage viewerName={viewerName} count={recipes.length} loading={loading} />;
  }
  // Empty state when there are no recipes (page 1 of a 1-page book).
  if (recipes.length === 0) {
    return <EmptyPage />;
  }
  // Index / TOC page (always index 1 when we have recipes).
  if (pageIndex === 1) {
    return <IndexPage recipes={recipes} cookStats={cookStats} pantry={pantry} onJumpTo={onJumpTo} viewerAllergies={viewerAllergies} />;
  }
  if (!recipe) return null;
  // Background-generation stub — render the generating / failed page
  // instead of the normal recipe page (the stub has no ingredients yet).
  const genStatus = recipe.recipe?._gen;
  if (genStatus === "pending" || genStatus === "failed") {
    return (
      <RecipeFlipCard recipe={recipe} userId={userId}>
        <GeneratingRecipePage
          recipe={recipe}
          status={genStatus}
          pageNumber={pageIndex - 1}
          totalRecipes={recipes.length}
          onContents={() => onJumpTo(1)}
          onRetry={() => onRetryGenerate?.(recipe.id)}
          onDelete={() => onDelete(recipe)}
        />
      </RecipeFlipCard>
    );
  }
  return (
    <RecipeFlipCard recipe={recipe} userId={userId}>
      <RecipePage
        recipe={recipe}
        userId={userId}
        hasFamily={hasFamily}
        isAdmin={isAdmin}
        viewerAllergies={viewerAllergies}
        pantry={pantry}
        pageNumber={pageIndex - 1}
        totalRecipes={recipes.length}
        cookStats={cookStats}
        allRecipes={recipes}
        onJumpTo={onJumpTo}
        onGenerateSide={onGenerateSide}
        onContents={() => onJumpTo(1)}
        onCook={(scaled) => onCook(recipe, scaled)}
        onSchedule={(scaled) => onSchedule(recipe, scaled)}
        onShareToggle={() => onShareToggle(recipe)}
        onPublishToggle={() => onPublishToggle(recipe)}
        onFavoriteToggle={() => onFavoriteToggle(recipe)}
        onSetNotes={(notes) => onSetNotes(recipe, notes)}
        onSetTags={(tags) => onSetTags(recipe, tags)}
        onRename={(newTitle) => onRename(recipe, newTitle)}
        onSetPairings={(pairings) => onSetPairings(recipe, pairings)}
        onDelete={() => onDelete(recipe)}
        onUpdateContent={(nextRecipe) => onUpdateContent(recipe, nextRecipe)}
        onForkSave={onForkSave}
        onRevise={() => onRevise(recipe)}
      />
    </RecipeFlipCard>
  );
}

// ── Cover page ──────────────────────────────────────────────────────
//
// Hardcover treatment. Edge-to-edge teal cloth with a cross-hatch
// woven texture (two diagonal repeating-linear-gradients), inset
// double-rule frame in white, foil-stamped embossed title centered,
// ornamental fleuron between title and author, and small corner
// accents stamped into each corner of the frame. White type
// throughout. Overrides the cream paper background of the page
// shell since this is a cover, not an interior page.

function CoverPage({ viewerName, count, loading }) {
  return (
    <div style={{
      position: "absolute", inset: 0,
      // Teal cloth. Diagonal gradient gives the impression of a
      // raked light source on book buckram. Two perpendicular
      // repeating-linear-gradients overlay a cross-weave at the
      // sub-pixel scale — reads as cloth, not as a flat color.
      background: `
        repeating-linear-gradient(45deg,
          rgba(255,255,255,0.030) 0px,
          rgba(255,255,255,0.030) 0.5px,
          transparent 0.5px,
          transparent 2.5px),
        repeating-linear-gradient(-45deg,
          rgba(0,0,0,0.045) 0px,
          rgba(0,0,0,0.045) 0.5px,
          transparent 0.5px,
          transparent 2.5px),
        radial-gradient(ellipse at 30% 20%, #3AA395 0%, #2A7E72 55%, #1F5E55 100%)
      `,
      // Subtle inset shadow gives the cover a slight bowl/depth.
      boxShadow: "inset 0 0 60px rgba(10,40,36,0.45), inset 0 1px 0 rgba(255,255,255,0.10)",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: "44px 32px",
      textAlign: "center",
      overflow: "hidden",
    }}>
      {/* Outer frame — thicker white rule. Inset by margin so the
          cloth shows at the spine + edges. */}
      <div
        aria-hidden
        style={{
          position: "absolute", inset: 18,
          border: "1.5px solid rgba(255,255,255,0.65)",
          borderRadius: 3,
          pointerEvents: "none",
        }}
      />
      {/* Inner frame — thinner rule, classic double-line book cover. */}
      <div
        aria-hidden
        style={{
          position: "absolute", inset: 24,
          border: "0.5px solid rgba(255,255,255,0.40)",
          borderRadius: 2,
          pointerEvents: "none",
        }}
      />

      {/* Corner accents — small foil-stamped triangles tucked into
          each frame corner. Pure CSS via rotated borders. */}
      <CornerAccent corner="tl" />
      <CornerAccent corner="tr" />
      <CornerAccent corner="bl" />
      <CornerAccent corner="br" />

      {/* Top kicker. */}
      <div style={{
        position: "absolute", top: 38, left: 0, right: 0,
        fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
        color: "rgba(255,255,255,0.78)",
        letterSpacing: "0.30em",
        textTransform: "uppercase",
      }}>
        Volume I
      </div>

      {/* Title — foil-stamped feel via layered text-shadow. */}
      <div style={{
        fontFamily: FONT.display, fontSize: 60,
        fontWeight: 500, color: "#FFFFFF", lineHeight: 0.92,
        padding: "0 16px",
        letterSpacing: "0.02em",
        textTransform: "uppercase",
        textShadow:
          "0 1px 0 rgba(255,255,255,0.18), 0 2px 4px rgba(10,40,36,0.55), 0 -1px 0 rgba(0,0,0,0.30)",
      }}>
        Recipe<br />Book
      </div>

      {/* Ornamental fleuron between title and author. Three glyphs
          spaced wide — reads as classical book-cover decoration. */}
      <div
        aria-hidden
        style={{
          marginTop: 26,
          display: "flex", alignItems: "center", gap: 14,
          color: "rgba(255,255,255,0.70)",
          fontSize: 13,
        }}
      >
        <span style={{ width: 28, height: 1, background: "rgba(255,255,255,0.55)" }} />
        <span style={{ fontFamily: FONT.display, lineHeight: 1, transform: "translateY(-1px)" }}>✦</span>
        <span style={{ width: 28, height: 1, background: "rgba(255,255,255,0.55)" }} />
      </div>

      {/* Author / "of [name]" — italic white Forma, the editorial
          subhead that grounds the cover in whose book this is. */}
      <div style={{
        marginTop: 24,
        fontFamily: FONT.forma, fontSize: 22,
        fontStyle: "italic", fontWeight: 500,
        color: "#FFFFFF", lineHeight: 1.25,
        textShadow: "0 1px 1px rgba(10,40,36,0.45)",
        padding: "0 12px",
      }}>
        of {viewerName || "the chef"}
      </div>

      {/* Bottom kicker — count + year, tracked uppercase, sits
          inside the frame above the spine. */}
      <div style={{
        position: "absolute", bottom: 42, left: 0, right: 0,
        fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
        color: "rgba(255,255,255,0.70)",
        letterSpacing: "0.22em",
        textTransform: "uppercase",
      }}>
        {loading
          ? "Opening the book"
          : count === 0
            ? "Empty · Awaiting first entry"
            : `${count} ${count === 1 ? "Recipe" : "Recipes"} · ${new Date().getFullYear()}`}
      </div>
    </div>
  );
}

// Small foil-stamped corner ornament for the cover frame. Drawn as
// two short rules meeting at a corner, inset slightly from the
// frame's edge — the typographic move every clothbound book uses.
function CornerAccent({ corner }) {
  // Position offsets keyed by corner. inset values match the inner
  // frame (24px) plus a small additional margin so the ornament
  // sits INSIDE the frame, not on top of it.
  const map = {
    tl: { top: 30, left: 30 },
    tr: { top: 30, right: 30 },
    bl: { bottom: 30, left: 30 },
    br: { bottom: 30, right: 30 },
  };
  const isTop  = corner === "tl" || corner === "tr";
  const isLeft = corner === "tl" || corner === "bl";
  return (
    <div
      aria-hidden
      style={{
        position: "absolute", ...map[corner],
        width: 14, height: 14,
        pointerEvents: "none",
        borderColor: "rgba(255,255,255,0.55)",
        borderStyle: "solid",
        borderWidth: 0,
        borderTopWidth:    isTop  ? 1 : 0,
        borderBottomWidth: isTop  ? 0 : 1,
        borderLeftWidth:   isLeft ? 1 : 0,
        borderRightWidth:  isLeft ? 0 : 1,
      }}
    />
  );
}

// ── Empty state (single-page book) ──────────────────────────────────

function EmptyPage() {
  return (
    <div style={{
      width: "100%", height: "100%",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: "40px 32px", textAlign: "center",
    }}>
      <div style={{
        fontFamily: FONT.forma, fontSize: 12, color: "#9C7E40",
        letterSpacing: "0.20em",
      }}>
        BLANK PAGES
      </div>
      <div style={{
        marginTop: 22, fontFamily: FONT.forma, fontSize: 24,
        fontStyle: "italic", fontWeight: 500,
        color: "#3A2A12", lineHeight: 1.3,
      }}>
        Save your first recipe and watch it land here.
      </div>
      <div style={{
        marginTop: 18, fontFamily: FONT.forma, fontSize: 12,
        color: "#7C5E2C", lineHeight: 1.6, maxWidth: 280,
      }}>
        Anything you cook through the AI generator or the custom
        builder will collect in your book — yours alone, organized
        in the order you wrote them.
      </div>
    </div>
  );
}

// ── Tags row ────────────────────────────────────────────────────────
//
// Chip row above the ingredients column. Each existing tag is a
// removable chip; a "+ tag" affordance opens an inline input that
// adds a new tag on Enter. Tags are normalized lowercase by the
// hook's setTags — we just display them title-cased.
function TagsRow({ tags = [], onChange }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const commit = () => {
    const cleaned = draft.trim();
    if (!cleaned) { setAdding(false); setDraft(""); return; }
    const next = Array.from(new Set([...(tags || []), cleaned.toLowerCase()]));
    onChange?.(next);
    setDraft("");
    setAdding(false);
  };
  if (!onChange) return null;
  return (
    <div style={{
      marginTop: 10, display: "flex", flexWrap: "wrap", gap: 5,
      alignItems: "center",
    }}>
      {(tags || []).map((tag) => (
        <span
          key={tag}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "3px 4px 3px 9px",
            background: "rgba(124,94,44,0.10)",
            border: "1px solid rgba(124,94,44,0.28)",
            color: "#5C3F18",
            borderRadius: 999,
            fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
            letterSpacing: "0.08em", textTransform: "uppercase",
          }}
        >
          {tag}
          <button
            onClick={() => onChange((tags || []).filter((t) => t !== tag))}
            aria-label={`Remove tag ${tag}`}
            style={{
              width: 16, height: 16, borderRadius: 8,
              background: "rgba(124,94,44,0.18)",
              border: "none", color: "#5C3F18",
              fontSize: 12, lineHeight: 1, padding: 0,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </span>
      ))}
      {adding ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            else if (e.key === "Escape") { setAdding(false); setDraft(""); }
          }}
          placeholder="weeknight"
          maxLength={24}
          style={{
            padding: "3px 9px",
            background: "rgba(255,250,236,0.8)",
            border: "1px solid rgba(124,94,44,0.45)",
            color: "#3A2A12",
            borderRadius: 999,
            fontFamily: FONT.forma, fontSize: 12, fontWeight: 500,
            letterSpacing: "0.04em",
            outline: "none",
            width: 110,
          }}
        />
      ) : (
        <button
          onClick={() => setAdding(true)}
          style={{
            padding: "3px 9px",
            background: "transparent",
            border: "1px dashed rgba(124,94,44,0.40)",
            color: "#7C5E2C",
            borderRadius: 999,
            fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
            letterSpacing: "0.10em", textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          + Tag
        </button>
      )}
    </div>
  );
}

// ── Notes editor ───────────────────────────────────────────────────
//
// Collapsed by default with a hint line; tap to expand into a
// textarea that saves on blur. 500ms debounce protects the network
// from per-keystroke writes; the hook's setNotes is fire-and-forget
// (no rollback) since rolling back the user's typing would be hostile.
function NotesEditor({ notes = "", onChange }) {
  const [draft, setDraft] = useState(notes || "");
  const [open, setOpen] = useState(false);
  // Sync with prop when the recipe changes (PageBody passes a fresh
  // recipe). Without this the editor would carry stale text across
  // navigations.
  useEffect(() => {
    setDraft(notes || "");
    setOpen(Boolean(notes));
  }, [notes]);
  // Debounced auto-save during typing. Same shape as the AIRecipe
  // draft persistence elsewhere in the codebase.
  useEffect(() => {
    if (!onChange) return;
    if (draft === (notes || "")) return;
    const t = setTimeout(() => onChange(draft), 500);
    return () => clearTimeout(t);
  }, [draft, notes, onChange]);
  if (!onChange) return null;
  return (
    <div style={{
      marginTop: 12,
      borderTop: "1px dashed rgba(124,94,44,0.22)",
      paddingTop: 10,
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 6,
        marginBottom: open ? 6 : 0,
      }}>
        <span style={{
          fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
          color: "#9C7E40", letterSpacing: "0.16em",
          textTransform: "uppercase",
        }}>
          Notes
        </span>
        {!open && (
          <button
            onClick={() => setOpen(true)}
            style={{
              fontFamily: FONT.forma, fontSize: 12, fontWeight: 500,
              fontStyle: "italic", color: "#7C5E2C",
              background: "transparent", border: "none",
              padding: 0, cursor: "pointer",
              letterSpacing: "0.02em",
            }}
          >
            {notes ? notes : "Tap to add a note…"}
          </button>
        )}
      </div>
      {open && (
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => { if (!draft) setOpen(false); }}
          placeholder="Doubled the garlic, much better…"
          rows={3}
          style={{
            width: "100%", boxSizing: "border-box",
            padding: "8px 10px",
            background: "rgba(255,250,236,0.72)",
            border: "1px solid rgba(124,94,44,0.28)",
            color: "#3A2A12",
            borderRadius: 6,
            fontFamily: FONT.forma, fontSize: 13,
            lineHeight: 1.5,
            fontStyle: "italic",
            outline: "none",
            resize: "vertical",
            minHeight: 60,
            maxHeight: 160,
          }}
        />
      )}
    </div>
  );
}

// ── Editable recipe title ───────────────────────────────────────────
//
// Renders the recipe page's title with the standard New Kansas italic
// treatment. When `onRename` is supplied (i.e. the viewer owns this
// recipe and isn't looking at a bundled / family-shared row), a small
// pencil affordance appears to the right of the title. Tap the pencil
// OR tap the title itself to flip into an input. Enter or blur saves;
// Escape cancels. Empty titles are silently rejected — we just exit
// edit mode without firing onRename, since "(untitled)" is never what
// the user wanted.
function EditableTitle({ title, onRename }) {
  const [editing, setEditing]   = useState(false);
  const [draft,   setDraft]     = useState(title || "");
  const inputRef = useRef(null);
  const editable = typeof onRename === "function";

  // When toggling INTO edit mode, sync the draft from the latest
  // title (so it picks up renames from realtime / scaling effects)
  // and select the input contents so a tap-to-rename starts ready
  // for typing-over.
  useEffect(() => {
    if (editing) {
      setDraft(title || "");
      // Defer the focus + select so the input has actually mounted.
      const t = setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
      return () => clearTimeout(t);
    }
  }, [editing, title]);

  const commit = () => {
    const trimmed = (draft || "").trim();
    if (trimmed && trimmed !== title) {
      try { onRename?.(trimmed); }
      catch (e) { console.error("[RecipeBook] rename failed:", e); }
    }
    setEditing(false);
  };

  const cancel = () => {
    setDraft(title || "");
    setEditing(false);
  };

  const titleStyle = {
    marginTop: 6,
    fontFamily: FONT.itemTitle,
    fontSize: 26,
    fontStyle: "italic",
    fontWeight: 300,
    color: "#3A2A12",
    lineHeight: 1.15,
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter")  { e.preventDefault(); commit(); }
          if (e.key === "Escape") { e.preventDefault(); cancel(); }
        }}
        maxLength={200}
        style={{
          ...titleStyle,
          width: "100%",
          background: "rgba(255,252,241,0.85)",
          border: "1px solid rgba(124,94,44,0.40)",
          borderRadius: 8,
          padding: "4px 8px",
          outline: "none",
          boxSizing: "border-box",
        }}
      />
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
      <div
        onClick={editable ? () => setEditing(true) : undefined}
        style={{
          ...titleStyle,
          flex: 1, minWidth: 0,
          cursor: editable ? "text" : "default",
        }}
      >
        {title || "(untitled)"}
      </div>
      {editable && (
        <button
          onClick={() => setEditing(true)}
          aria-label="Rename recipe"
          style={{
            flexShrink: 0,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 28, height: 28, borderRadius: 14,
            background: "transparent",
            border: "1px solid rgba(124,94,44,0.28)",
            color: "#7C5E2C",
            cursor: "pointer", padding: 0,
            marginBottom: 2,
            transition: "background 180ms ease",
          }}
        >
          {/* Pencil — inline SVG so glyphs render consistently across platforms. */}
          <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden focusable="false">
            <path
              d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0L15.13 5.12l3.75 3.75 1.83-1.83z"
              fill="currentColor"
            />
          </svg>
        </button>
      )}
    </div>
  );
}

// ── Recipe page ─────────────────────────────────────────────────────

function RecipePage({
  recipe, userId, hasFamily, isAdmin = false, pantry = [], pageNumber, totalRecipes, cookStats,
  allRecipes = [], onJumpTo, onGenerateSide, viewerAllergies = [],
  onContents, onCook, onSchedule, onShareToggle, onPublishToggle,
  onFavoriteToggle, onSetNotes, onSetTags, onRename, onSetPairings, onDelete,
  onUpdateContent, onForkSave, onRevise, viewerName,
}) {
  const recipeStats = cookStats?.get(recipe.slug) || null;
  // Re-render when the user flips US ↔ metric so the ingredient
  // amounts below track the preference (data-adaptability rule:
  // stored amounts stay source-verbatim, display transforms here).
  useUnitPrefsVersion();
  // In-context edit mode — the page swaps to RecipePageEditor. Exits
  // on page turn (recipe.id change) so a half-edit can't leak onto a
  // different recipe.
  const [editing, setEditing] = useState(false);
  // MAKE IT MINE inline reveal (edit-by-hand vs fix-with-AI). Both
  // reset on page turn so a half-open chooser can't leak across
  // recipes.
  const [makeMineOpen, setMakeMineOpen] = useState(false);
  // Past-versions sheet (recipe.versions[], written by every in-place
  // content save). Resets on page turn.
  const [versionsOpen, setVersionsOpen] = useState(false);
  // Send-a-copy busy latch (share-row mint + OS share sheet).
  const [sendingCopy, setSendingCopy] = useState(false);
  // Share sheet — opened by the top-right icon; the same surface Cook
  // Mode uses (family/friend direct sends + OS text/email/link path).
  const [shareSheetOpen, setShareSheetOpen] = useState(false);
  const [sentShareIds, setSentShareIds] = useState(() => new Set());
  const [copiedShareLink, setCopiedShareLink] = useState(false);
  const { family, friends } = useRelationships(userId);
  const { push: pushToast } = useToast();
  useEffect(() => {
    setEditing(false); setMakeMineOpen(false); setVersionsOpen(false);
    setShareSheetOpen(false); setSentShareIds(new Set());
  }, [recipe.id]);
  // Author byline — the recipe owner's name + avatar. The book shows
  // the viewer's own rows, so this is usually them; resolve from the
  // row's userId so forked/shared rows still attribute correctly.
  const authorId = recipe.userId || userId;
  const { profile: authorProfile } = useProfile(authorId);
  const authorName = authorProfile?.name || authorProfile?.username || "You";
  const authorAvatar = absoluteAvatarURL(authorProfile?.avatar_url);
  // Direct send to a family member / friend — mints a recipe_shares
  // row for that person; the notifications trigger pushes it and the
  // tap opens the accept card (App.jsx target_kind 'recipe_share').
  const sendCopyTo = async (recipientId) => {
    if (!recipientId || sentShareIds.has(recipientId)) return;
    // Optimistic ✓ SENT so the row flips before the round-trip.
    setSentShareIds(prev => new Set(prev).add(recipientId));
    try {
      await createRecipeShare({
        recipe: recipe.recipe,
        senderName: viewerName || authorName || null,
        recipientId,
      });
    } catch (e) {
      setSentShareIds(prev => {
        const next = new Set(prev);
        next.delete(recipientId);
        return next;
      });
      console.error("[recipeBook] direct share failed:", e);
      pushToast(e?.message || "Couldn't send that — try again.", { emoji: "⚠️", kind: "warn" });
    }
  };
  // Text/email/link — mint an open link share and hand
  // booda.app/r/<token> to the OS share sheet; clipboard fallback on
  // web so the link is never lost after the row was minted.
  const shareCopyNative = async () => {
    if (sendingCopy) return;
    setSendingCopy(true);
    try {
      const { url } = await createRecipeShare({
        recipe: recipe.recipe,
        senderName: viewerName || authorName || null,
      });
      const title = recipe.recipe?.title || "A recipe";
      try {
        await Share.share({
          title,
          text: `${title} — a recipe for you on Booda`,
          url,
        });
      } catch (shareErr) {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(url);
          setCopiedShareLink(true);
          setTimeout(() => setCopiedShareLink(false), 1800);
        }
      }
    } catch (e) {
      console.error("[recipeBook] send-copy failed:", e);
      pushToast(e?.message || "Couldn't create the share link — try again.", { emoji: "⚠️", kind: "warn" });
    } finally {
      setSendingCopy(false);
    }
  };
  // Household cook count — family-visible cook_logs clustered by slug
  // (30-min cached; zero queries for family-less users). Renders the
  // "across the family" line under the viewer's own count when the
  // family total is larger.
  const { bySlug: familyCookStats } = useFamilyCookStats(hasFamily ? userId : null);
  const familyCookCount = familyCookStats.get(recipe.slug)?.cookCount || 0;
  const baseRecipe = recipe.recipe || {};
  const baseServes = Number(baseRecipe.serves) || 0;
  // Local servings override, bounded by the app-wide MAX_SERVINGS
  // (recipeConstants) like every other servings surface — this page
  // used to hard-cap at 12 while pre-cook/schedule allowed 100, so
  // the book's choice couldn't express a real crowd cook. The chosen
  // value rides into COOK NOW / SCHEDULE below.
  const [servingsOverride, setServingsOverride] = useState(baseServes || null);
  // Reset override when navigating to a different recipe.
  useEffect(() => { setServingsOverride(baseServes || null); }, [recipe.id, baseServes]);

  const targetServes = servingsOverride || baseServes || null;
  const r = useMemo(() => {
    if (!targetServes || targetServes === baseServes) return baseRecipe;
    return scaleRecipe(baseRecipe, targetServes) || baseRecipe;
  }, [baseRecipe, baseServes, targetServes]);

  // Nutrition rollup. Uses scaled recipe `r` so total kcal reflects
  // the user's chosen servings count.
  const { get: getBrandNutrition } = useBrandNutrition();
  const brandNutrition = useMemo(
    () => ({ get: (k) => getBrandNutrition?.(k) || null }),
    [getBrandNutrition],
  );
  const ingredientInfo = useIngredientInfo();

  const ingredientCount = Array.isArray(r.ingredients) ? r.ingredients.length : 0;
  const stepCount = Array.isArray(r.steps) ? r.steps.length : 0;
  const meta = useMemo(() => {
    const bits = [];
    if (r.cuisine) bits.push(r.cuisine.toUpperCase());
    const tt = totalTimeMin(r);
    if (tt) bits.push(`${tt} MIN`);
    if (typeof r.difficulty === "number") bits.push(difficultyLabel(r.difficulty).toUpperCase());
    return bits.join(" · ");
  }, [r]);

  // Edit mode swaps the whole page for the editor (same paper, same
  // ink, editable fields). Lives in its own file per the file-size
  // discipline; save semantics (in-place vs fork) live in
  // src/lib/recipeEdits.js.
  if (editing) {
    return (
      <RecipePageEditor
        bookRow={recipe}
        userId={userId}
        onSaveInPlace={(id, nextRecipe) => onUpdateContent?.(nextRecipe)}
        onForkSave={onForkSave}
        onExit={() => setEditing(false)}
      />
    );
  }

  return (
    <div style={{
      width: "100%", height: "100%",
      display: "flex", flexDirection: "column",
      padding: "26px 28px 18px",
      position: "relative",
      overflowY: "auto", overflowX: "hidden",
      WebkitOverflowScrolling: "touch",
    }}>
      {/* Share — top-right of every recipe page (replaced the AI /
          human mascot stamp). Opens the same share sheet Cook Mode
          uses: family/friend direct sends + the OS text/email/link
          path (booda.app/r/<token> link shares). */}
      <button
        onClick={() => setShareSheetOpen(true)}
        aria-label="Share this recipe"
        className="mcm-focusable"
        style={{
          position: "absolute", top: 20, right: 30, zIndex: 2,
          width: 48, height: 48, borderRadius: 24,
          background: sendingCopy ? "rgba(195,144,35,0.18)" : "rgba(124,94,44,0.10)",
          border: "1px solid rgba(124,94,44,0.35)",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", padding: 0, touchAction: "manipulation",
        }}
      >
        {/* OS-style share glyph — arrow rising out of a tray. */}
        <svg width="21" height="21" viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
          <path d="M12 3.2v11.3" stroke="#5C3F18" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M8.2 6.6L12 3l3.8 3.6" stroke="#5C3F18" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M8.4 10.2H6.6c-.9 0-1.6.7-1.6 1.6v6.6c0 .9.7 1.6 1.6 1.6h10.8c.9 0 1.6-.7 1.6-1.6v-6.6c0-.9-.7-1.6-1.6-1.6h-1.8" stroke="#5C3F18" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </button>
      {shareSheetOpen && createPortal(
        <ShareRecipeSheet
          recipe={recipe.recipe}
          family={family}
          friends={friends}
          sentIds={sentShareIds}
          onSend={sendCopyTo}
          onShareNative={shareCopyNative}
          copiedLink={copiedShareLink}
          onClose={() => setShareSheetOpen(false)}
        />,
        document.body,
      )}

      {/* Recipe header photo strip — sits between the share button
          (absolute, top-right) and the title block. Renders a hero
          photo + upload control when there are uploads, or a quiet
          "+ ADD A PHOTO" CTA when empty. Hidden for AI / synthetic
          recipes without an actual slug to attach photos to. */}
      {recipe?.slug && userId && (
        <div style={{ marginTop: 4 }}>
          <RecipePhotoStrip recipeSlug={recipe.slug} userId={userId} />
        </div>
      )}

      {/* Title block. Reserve a right-padding column for the
          mascot stamp on every recipe page (AI: Chadwick, human:
          mama) so a long title doesn't run into the badge. */}
      <div style={{
        marginTop: 12,
        paddingRight: 96,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {onContents && (
            <button
              onClick={onContents}
              aria-label="Back to table of contents"
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                background: "rgba(124,94,44,0.08)",
                border: "1px solid rgba(124,94,44,0.30)",
                borderRadius: 999,
                padding: "4px 10px",
                fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
                color: "#5C3F18",
                letterSpacing: "0.14em", textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              <span style={{ fontSize: 12, lineHeight: 1, marginTop: -1 }}>↩</span>
              Contents
            </button>
          )}
          {/* Favorite star — uses inline SVG (typographic glyphs
              like ★ render inconsistently across platforms). Filled
              gold when favorited, outline when not. Aria label flips
              based on state for screen readers. */}
          {onFavoriteToggle && (
            <button
              onClick={onFavoriteToggle}
              aria-label={recipe.isFavorite ? "Remove from favorites" : "Add to favorites"}
              style={{
                marginLeft: "auto",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 30, height: 30, borderRadius: 15,
                background: recipe.isFavorite ? "rgba(195,144,35,0.18)" : "transparent",
                border: `1px solid ${recipe.isFavorite ? "rgba(195,144,35,0.55)" : "rgba(124,94,44,0.28)"}`,
                cursor: "pointer", padding: 0,
                transition: "background 180ms ease, border-color 180ms ease",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden focusable="false">
                <path
                  d="M12 2.6l2.93 6.06 6.62.96-4.79 4.74 1.13 6.66L12 17.86 6.11 21.02l1.13-6.66L2.45 9.62l6.62-.96L12 2.6z"
                  fill={recipe.isFavorite ? "#C39023" : "none"}
                  stroke={recipe.isFavorite ? "#9C7E40" : "#7C5E2C"}
                  strokeWidth="1.6"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>
        <EditableTitle
          title={r.title}
          onRename={onRename}
        />
        {/* Author byline — whose recipe this is, with their avatar. */}
        <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <div style={{
            width: 26, height: 26, borderRadius: 13, flexShrink: 0,
            overflow: "hidden", background: "rgba(124,94,44,0.16)",
            border: "1px solid rgba(124,94,44,0.30)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {authorAvatar ? (
              <img src={authorAvatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <span style={{ fontFamily: FONT.forma, fontSize: 12, fontWeight: 700, color: "#7C5E2C" }}>
                {String(authorName).charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <span style={{ fontFamily: FONT.forma, fontSize: 13, fontWeight: 600, color: "#5C3F18" }}>
            by {authorName}
          </span>
          {/* Past versions — only when this recipe has edit history. */}
          {Array.isArray(baseRecipe.versions) && baseRecipe.versions.length > 0 && (
            <button
              type="button"
              className="mcm-focusable"
              onClick={() => setVersionsOpen(true)}
              style={{
                background: "none", border: "none", padding: "2px 0",
                fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
                color: "#7C5E2C", textDecoration: "underline", textUnderlineOffset: 3,
                textDecorationColor: "rgba(124,94,44,0.4)",
                cursor: "pointer", touchAction: "manipulation",
              }}
            >
              ↺ {baseRecipe.versions.length} past version{baseRecipe.versions.length === 1 ? "" : "s"}
            </button>
          )}
        </div>
        {meta && (
          <div style={{
            marginTop: 6, fontFamily: FONT.forma, fontSize: 12,
            color: "#7C5E2C", letterSpacing: "0.10em",
          }}>
            {meta}
          </div>
        )}
        {/* Provenance — forked recipes celebrate their lineage AND
            the owner's customization. forkedFrom is stamped by the
            fork paths (CookComplete swaps, RecipePageEditor, the
            revise sheet); tweaks carries summarizeDiff counts. */}
        {baseRecipe.forkedFrom?.title && (
          <div style={{
            marginTop: 5, display: "flex", alignItems: "center",
            gap: 8, flexWrap: "wrap",
          }}>
            <span style={{
              fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
              color: "#7C5E2C", letterSpacing: "0.10em",
            }}>
              BASED ON {String(baseRecipe.forkedFrom.title).toUpperCase()}
            </span>
            {Number(baseRecipe.forkedFrom?.tweaks?.total) > 0 && (
              <span style={{
                fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
                color: "#8A6A1B", background: "rgba(195,144,35,0.16)",
                border: "1px solid rgba(195,144,35,0.45)",
                borderRadius: 999, padding: "2px 9px",
                letterSpacing: "0.08em",
              }}>
                {baseRecipe.forkedFrom.tweaks.total} TWEAK{baseRecipe.forkedFrom.tweaks.total === 1 ? "" : "S"} OF MINE
              </span>
            )}
          </div>
        )}
        {/* Side-dish provenance — "SIDE FOR {MAIN} ›", tappable back
            to the main. Derived from the mains' sideDishes links so
            it stays fresh across renames and server-side persists. */}
        <PairedWithAttribution row={recipe} allRecipes={allRecipes} onJumpTo={onJumpTo} />
        {recipeStats?.cookCount > 0 && (
          <div style={{
            marginTop: 4, fontFamily: FONT.forma, fontSize: 12,
            fontWeight: 600,
            color: "#246A60", letterSpacing: "0.10em",
            textTransform: "uppercase",
          }}>
            Cooked {recipeStats.cookCount}× ·{" "}
            {relativeTimeShort(recipeStats.lastCookedAt) || "in your history"}
          </div>
        )}
        {familyCookCount > (recipeStats?.cookCount || 0) && (
          <div style={{
            marginTop: 3, fontFamily: FONT.forma, fontSize: 12,
            fontWeight: 600,
            color: "#246A60", letterSpacing: "0.10em",
            textTransform: "uppercase",
          }}>
            Cooked {familyCookCount}× across the family
          </div>
        )}

        {/* Servings stepper — the shared ServingsStepper (typeable,
            ×2/×3, MAX_SERVINGS cap) so the book matches pre-cook /
            schedule instead of its old 12-cap one-off. Scales the
            ingredient amounts in-place via scaleRecipe(); the chosen
            value also rides into COOK NOW / SCHEDULE. Hidden when
            the recipe doesn't specify a serves count (no base = no
            scale factor). */}
        {baseServes > 0 && (
          <div style={{
            marginTop: 8,
            display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap",
          }}>
            <ServingsStepper
              value={targetServes || baseServes}
              onChange={(n) => setServingsOverride(n)}
              label="SERVES"
              ariaLabel="Servings"
              palette={{
                surface: "rgba(255,250,236,0.9)",
                btnBg: "rgba(124,94,44,0.12)",
                btnBorder: "rgba(124,94,44,0.30)",
                accent: "#8A6A1B",
                accentSoft: "rgba(195,144,35,0.16)",
                ink: "#3A2A12",
                muted: "#9C7E40",
                fontLabel: FONT.forma,
                fontNum: FONT.forma,
              }}
            />
            {servingsOverride && servingsOverride !== baseServes && (
              <button
                onClick={() => setServingsOverride(baseServes)}
                aria-label="Reset servings"
                style={{
                  fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
                  color: "#9C7E40", letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  background: "transparent", border: "none",
                  padding: "8px 4px", margin: "-8px 0", cursor: "pointer",
                }}
              >
                Reset
              </button>
            )}
          </div>
        )}

        {r.subtitle && (
          <div style={{
            marginTop: 10, fontFamily: FONT.forma, fontSize: 15,
            fontWeight: 500,
            color: "#5C3F18", lineHeight: 1.45, fontStyle: "italic",
          }}>
            {r.subtitle}
          </div>
        )}
        <RecipeNutritionLine
          recipe={r}
          pantry={pantry}
          ingredientInfo={ingredientInfo}
          brandNutrition={brandNutrition}
          compact
        />
      </div>

      {/* Pairing suggestions. Lazy-loaded — empty state shows a
          "Suggest a pairing" CTA only; the AI call fires only when
          the user taps. Result is cached on recipe.pairings so
          subsequent views read from cache with zero network cost.
          Sits between the nutrition line and the tags row in the
          metadata zone, after numeric facts and before user tags. */}
      <RecipePairingCard recipe={recipe} onSetPairings={onSetPairings} viewerAllergies={viewerAllergies} />

      {/* Intro narration — plays only if an admin pre-generated audio
          via the generate-recipe-narration flow. Renders nothing
          when the recipe has no audio.intro stamped on it. Per-step
          audio for cook-mode hands-free playback is handled in
          CookMode separately. */}
      <RecipeAudioPlayer audio={recipe.recipe?.audio} />

      {/* Admin-only "Generate narration" button. Renders nothing for
          non-admins. ElevenLabs is paid and expensive — admin gates
          it; the result is cached forever on recipe.audio so all
          users benefit from one-time generation cost. */}
      <RecipeNarrationButton recipe={recipe} isAdmin={isAdmin} />

      {/* Tags — chip row above the ingredients body. Sits in the
          metadata zone (alongside cuisine/time/difficulty), not
          inside the ingredients column. */}
      <TagsRow tags={recipe.tags} onChange={onSetTags} />

      {/* Body — ingredients column. Page itself scrolls (outer
          container has overflowY:auto), so this just lays the
          ingredient list naturally; multi-column balances by
          content height. */}
      <div style={{
        marginTop: 16,
        display: "flex", flexDirection: "column",
      }}>
        <div style={{
          fontFamily: FONT.forma, fontSize: 12, color: "#9C7E40",
          letterSpacing: "0.16em", marginBottom: 6,
        }}>
          INGREDIENTS · {ingredientCount}
        </div>
        <div style={{
          fontFamily: FONT.forma, fontSize: 13, color: "#3A2A12",
          lineHeight: 1.7, columnCount: ingredientCount > 6 ? 2 : 1,
          columnGap: 18,
        }}>
          {(r.ingredients || []).map((ing, i) => (
            <div key={i} style={{ breakInside: "avoid", marginBottom: 4 }}>
              <span style={{ color: "#7C5E2C", fontStyle: "italic" }}>
                {ing.amount ? `${applyPreferredUnit(ing.amount, ing, DISPLAY_CONTEXT.COOK) || ing.amount} ` : ""}
              </span>
              {ing.item || ing.name || "(ingredient)"}
            </div>
          ))}
        </div>
        {stepCount > 0 && (
          <div style={{
            marginTop: 12, fontFamily: FONT.forma, fontSize: 12,
            color: "#9C7E40", letterSpacing: "0.10em",
          }}>
            {stepCount} STEP{stepCount === 1 ? "" : "S"} · TAP COOK NOW TO BEGIN
          </div>
        )}
      </div>

      {/* Personal notes — collapsed hint until tapped, then a
          textarea that auto-saves 500ms after the last keystroke. */}
      <NotesEditor notes={recipe.personalNotes} onChange={onSetNotes} />

      {/* Side dishes — paired-course list + opt-in AI generation.
          Gated off course-typed non-mains so sides don't offer
          sides-of-sides; a missing course reads as a main. */}
      {onGenerateSide
        && !["side", "dessert", "appetizer", "bake", "prep"].includes(baseRecipe.course || "")
        && (
          <RecipeSideDishSection
            mainRow={recipe}
            allRecipes={allRecipes}
            pantry={pantry}
            userId={userId}
            onJumpTo={onJumpTo}
            onGenerateSide={onGenerateSide}
          />
        )}

      {/* CTA strip. COOK NOW is the primary action; everything else
          is secondary or affordance-style. */}
      <div style={{
        marginTop: 14, display: "flex", flexDirection: "column", gap: 8,
      }}>
        <button
          // Hand off the BASE recipe with the stepper's pick riding as
          // _initialServings — CookMode seeds its own servings override
          // from it. Passing a pre-scaled copy instead would collapse
          // CookMode's step-prose scale ratio to 1 (prose says "4
          // chops" while ingredients read 18×); passing the bare
          // handler dropped the choice entirely.
          onClick={() => onCook(
            targetServes && targetServes !== baseServes
              ? { ...baseRecipe, _initialServings: targetServes }
              : baseRecipe,
          )}
          style={{
            width: "100%", padding: "14px",
            background: "linear-gradient(180deg, #C85A1F 0%, #A34711 100%)",
            border: "1px solid rgba(80,40,10,0.5)",
            color: "#FFF8EE", borderRadius: 10,
            fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
            letterSpacing: "0.14em", cursor: "pointer",
            boxShadow: "0 6px 14px rgba(168,73,17,0.35)",
          }}
        >
          COOK NOW →
        </button>
        {/* Second rail — two actions max. Everything that used to
            crowd this row (EDIT/REVISE/SHARE/PUBLISH/REMOVE pills)
            either folded into MAKE IT MINE or quieted into the text
            row below. Three tiers: do (COOK NOW), plan + own (this
            row), housekeeping (links). */}
        <div style={{ display: "flex", gap: 8 }}>
          {onSchedule && (
            <button
              onClick={() => onSchedule(r)}
              className="mcm-focusable"
              style={{
                flex: 1, padding: "12px",
                background: "rgba(124,94,44,0.10)",
                border: "1px solid rgba(124,94,44,0.30)",
                color: "#5C3F18", borderRadius: 8,
                fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
                letterSpacing: "0.10em", cursor: "pointer",
                touchAction: "manipulation",
              }}
            >
              SCHEDULE
            </button>
          )}
          <button
            onClick={() => setMakeMineOpen((v) => !v)}
            className="mcm-focusable"
            aria-expanded={makeMineOpen}
            style={{
              flex: 1, padding: "12px",
              background: makeMineOpen ? "rgba(124,94,44,0.18)" : "rgba(124,94,44,0.10)",
              border: `1px solid rgba(124,94,44,${makeMineOpen ? "0.45" : "0.30"})`,
              color: "#5C3F18", borderRadius: 8,
              fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
              letterSpacing: "0.10em", cursor: "pointer",
              touchAction: "manipulation",
            }}
          >
            MAKE IT MINE {makeMineOpen ? "▴" : "▾"}
          </button>
        </div>

        {/* MAKE IT MINE — inline reveal, not a menu icon and not a
            modal. Each path explains itself in plain words because
            "EDIT" vs "REVISE" meant nothing to an actual user. */}
        {makeMineOpen && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <button
              onClick={() => { setMakeMineOpen(false); setEditing(true); }}
              className="mcm-focusable"
              style={{
                textAlign: "left", padding: "11px 14px",
                background: "rgba(255,255,255,0.5)",
                border: "1px solid rgba(124,94,44,0.30)",
                borderRadius: 10, cursor: "pointer",
                touchAction: "manipulation",
              }}
            >
              <div style={{ fontFamily: FONT.forma, fontSize: 12, fontWeight: 600, color: "#5C3F18", letterSpacing: "0.10em" }}>
                EDIT IT MYSELF
              </div>
              <div style={{ marginTop: 2, fontFamily: FONT.forma, fontSize: 12, color: "#7C5E2C" }}>
                Change amounts, ingredients, or the wording of a step.
              </div>
            </button>
            {onRevise && (
              <button
                onClick={() => { setMakeMineOpen(false); onRevise(); }}
                className="mcm-focusable"
                style={{
                  textAlign: "left", padding: "11px 14px",
                  background: "rgba(255,255,255,0.5)",
                  border: "1px solid rgba(124,94,44,0.30)",
                  borderRadius: 10, cursor: "pointer",
                  touchAction: "manipulation",
                }}
              >
                <div style={{ fontFamily: FONT.forma, fontSize: 12, fontWeight: 600, color: "#5C3F18", letterSpacing: "0.10em" }}>
                  FIX IT WITH AI
                </div>
                <div style={{ marginTop: 2, fontFamily: FONT.forma, fontSize: 12, color: "#7C5E2C" }}>
                  Say what was off: "too spicy" · "didn't rise" · "simplify step 3".
                </div>
              </button>
            )}
          </div>
        )}

        {/* Housekeeping — set-and-forget toggles + the destructive
            action, demoted from pills to quiet text links. State
            reads in the label, not a ✓-pill. */}
        <div style={{
          display: "flex", flexWrap: "wrap", gap: "4px 16px",
          marginTop: 2, paddingLeft: 2,
        }}>
          {hasFamily && (
            <button
              onClick={onShareToggle}
              className="mcm-focusable"
              style={{
                background: "none", border: "none", padding: "4px 0",
                fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
                color: recipe.shared ? "#3F6328" : "#7C5E2C",
                textDecoration: "underline", textUnderlineOffset: 3,
                textDecorationColor: "rgba(124,94,44,0.35)",
                cursor: "pointer", touchAction: "manipulation",
              }}
            >
              {recipe.shared ? "Shared with family ✓" : "Share with family"}
            </button>
          )}
          {/* Publish to Community — separate from family-share. Author-
              only opt-in; counts cook_logs as leaderboard votes
              (migration 0236). */}
          {onPublishToggle && (
            <button
              onClick={onPublishToggle}
              className="mcm-focusable"
              style={{
                background: "none", border: "none", padding: "4px 0",
                fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
                color: recipe.communityPublished ? "#7A5A14" : "#7C5E2C",
                textDecoration: "underline", textUnderlineOffset: 3,
                textDecorationColor: "rgba(124,94,44,0.35)",
                cursor: "pointer", touchAction: "manipulation",
              }}
            >
              {recipe.communityPublished ? "On Community ✓" : "Publish to Community"}
            </button>
          )}
          <button
            onClick={onDelete}
            aria-label="Delete recipe"
            className="mcm-focusable"
            style={{
              background: "none", border: "none", padding: "4px 0",
              fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
              color: "#A04230",
              textDecoration: "underline", textUnderlineOffset: 3,
              textDecorationColor: "rgba(160,66,48,0.35)",
              cursor: "pointer", touchAction: "manipulation",
            }}
          >
            Remove
          </button>
        </div>

        {/* Kid-menu tag — parent marks this recipe as something the
            kids can request from their Foods tab. Writes to
            kid_recipes (mark / unmark / updateMeta). Sits below the
            primary action rail so it's discoverable without
            crowding the COOK NOW row. */}
        {recipe?.slug && userId && (
          <RecipeKidMenuSection recipe={recipe} userId={userId} />
        )}
      </div>

      {/* Page number — bottom-right where book pages traditionally
          sit. Inset slightly inward so it clears the corner curl
          decoration (rendered at the very corner by the parent
          motion.div). Reads as a foliation mark, not a label. */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          right: 22, bottom: 8,
          fontFamily: FONT.forma, fontSize: 12, fontWeight: 500,
          color: "#9C7E40",
          letterSpacing: "0.18em",
          pointerEvents: "none",
          zIndex: 1,
        }}
      >
        {String(pageNumber).padStart(2, "0")} OF {String(totalRecipes).padStart(2, "0")}
      </div>

      {/* Past-versions history. Restore routes through onUpdateContent,
          which snapshots the CURRENT version first — so restoring is
          itself non-destructive and reversible. */}
      {versionsOpen && (
        <RecipeVersionsSheet
          versions={baseRecipe.versions || []}
          onClose={() => setVersionsOpen(false)}
          onRestore={async (v) => {
            if (!v?.recipe || !onUpdateContent) return;
            // Single-arg convention: the prop closes over the row.
            try { await onUpdateContent(v.recipe); } catch (e) {
              console.warn("[RecipePage] restore version failed:", e?.message || e);
            }
            setVersionsOpen(false);
          }}
        />
      )}
    </div>
  );
}

// ── Page corner curl decoration ────────────────────────────────────

function CornerCurl() {
  return (
    <div
      aria-hidden
      style={{
        position: "absolute", right: 0, bottom: 0,
        width: 36, height: 36,
        pointerEvents: "none",
        background:
          "linear-gradient(135deg, transparent 50%, rgba(60,40,18,0.12) 50.5%, rgba(60,40,18,0.18) 100%)",
        borderTopLeftRadius: 6,
      }}
    />
  );
}

// ── Bottom nav ─────────────────────────────────────────────────────

function NavBar({ pageIndex, totalPages, onPrev, onNext }) {
  return (
    <div style={{
      marginTop: 14,
      display: "flex", alignItems: "center", justifyContent: "space-between",
    }}>
      <button
        onClick={onPrev}
        disabled={pageIndex === 0}
        aria-label="Previous page"
        style={{
          width: 44, height: 44, borderRadius: 22,
          background: pageIndex === 0 ? "transparent" : "rgba(245,237,219,0.18)",
          border: `1px solid rgba(245,237,219,${pageIndex === 0 ? 0.10 : 0.30})`,
          color: "#F8F1DC",
          opacity: pageIndex === 0 ? 0.35 : 1,
          cursor: pageIndex === 0 ? "default" : "pointer",
          fontFamily: FONT.forma, fontSize: 18, fontWeight: 500,
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 0, lineHeight: 1,
        }}
      >
        ‹
      </button>
      <div style={{
        fontFamily: FONT.forma, fontSize: 12, color: "rgba(245,237,219,0.75)",
        letterSpacing: "0.16em",
      }}>
        {String(pageIndex + 1).padStart(2, "0")} / {String(totalPages).padStart(2, "0")}
      </div>
      <button
        onClick={onNext}
        disabled={pageIndex >= totalPages - 1}
        aria-label="Next page"
        style={{
          width: 44, height: 44, borderRadius: 22,
          background: pageIndex >= totalPages - 1 ? "transparent" : "rgba(245,237,219,0.18)",
          border: `1px solid rgba(245,237,219,${pageIndex >= totalPages - 1 ? 0.10 : 0.30})`,
          color: "#F8F1DC",
          opacity: pageIndex >= totalPages - 1 ? 0.35 : 1,
          cursor: pageIndex >= totalPages - 1 ? "default" : "pointer",
          fontFamily: FONT.forma, fontSize: 18, fontWeight: 500,
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 0, lineHeight: 1,
        }}
      >
        ›
      </button>
    </div>
  );
}

// ── Delete confirm ─────────────────────────────────────────────────


// ── Delete confirm ─────────────────────────────────────────────────

function DeleteConfirm({ recipe, onCancel, onConfirm }) {
  if (!recipe) return null;
  const title = recipe.recipe?.title || "this recipe";
  return (
    <div
      onClick={onCancel}
      style={{
        position: "absolute", inset: 0,
        background: "rgba(20,12,4,0.70)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24, zIndex: 10,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 320,
          background: "#FBF5E8",
          border: "1px solid rgba(124,94,44,0.30)",
          borderRadius: 14,
          padding: "22px 22px 18px",
          boxShadow: "0 24px 48px rgba(20,12,4,0.4)",
        }}
      >
        <div style={{
          fontFamily: FONT.forma, fontSize: 12, color: "#A04230",
          letterSpacing: "0.16em",
        }}>
          REMOVE FROM BOOK
        </div>
        <div style={{
          marginTop: 6, fontFamily: FONT.forma, fontSize: 16,
          fontStyle: "italic", fontWeight: 600,
          color: "#3A2A12", lineHeight: 1.35,
        }}>
          Tear "{title}" out of the book?
        </div>
        <div style={{
          marginTop: 10, fontFamily: FONT.forma, fontSize: 12,
          color: "#7C5E2C", lineHeight: 1.5,
        }}>
          Removes this recipe from your book and the kitchen library.
          Cooks you've already logged stay in your history.
        </div>
        <div style={{
          marginTop: 18, display: "flex", gap: 8,
        }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1, padding: "12px",
              background: "transparent",
              border: "1px solid rgba(124,94,44,0.30)",
              color: "#5C3F18", borderRadius: 8,
              fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
              letterSpacing: "0.12em", cursor: "pointer",
            }}
          >
            KEEP IT
          </button>
          <button
            onClick={onConfirm}
            style={{
              flex: 1, padding: "12px",
              background: "rgba(180,60,40,0.92)",
              border: "1px solid rgba(120,30,15,0.6)",
              color: "#FFF8EE", borderRadius: 8,
              fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
              letterSpacing: "0.12em", cursor: "pointer",
            }}
          >
            REMOVE
          </button>
        </div>
      </div>
    </div>
  );
}

// Suppress unused-COLOR import warning while still keeping the
// import line near the top for consistency with other components
// in the codebase. COLOR isn't referenced because this component
// uses inline cream-and-brown ink-and-paper colors that don't map
// cleanly to the dark-theme MCM palette. If we later introduce a
// "paper" theme to lib/tokens.js, swap the literals for tokens.
void COLOR;
